import React, { useEffect, useState } from 'react';
import QuestionGroup from './QuestionGroups/QuestionGroup';
import { Col, Row, Button } from 'reactstrap';
import LabelWithTooltipComponent from './QuestionGroups/Questions/Components/LabelWithTooltipComponent';
import { QUOTE_FUNNEL, questionTypeLabels } from 'helpers/sf_constants';
import LoadingSpinner from 'components/Common/LoadingSpinner';
import useClearableTimeout from 'hooks/useClearableTimeout';
import { validate_email_request, validate_phone_request } from 'helpers/quote_funnel_helper';
import SfCustomCheckBox from 'components/Common/ActiveButton/SfCustomCheckBox';
import DOMPurify from 'dompurify';
import { useDispatch, useSelector } from 'react-redux';
import { concentFormSubmit } from 'store/consent-confirmation/action';

const CONTINUE_AND_GET_QUOTE_TEXT = 'Continue & Get Quote';
const CONTINUOUS_INSURANCE = 'continuous_insurance'
const CARRIER = 'carrier'
const Page = ({ page, answers, onChangeEvent, nextPage, isLastPageGroup,
  isLastPage, quote_funnel_questions_loading, review, tcpa_disclaimer_text
}) => {
  const dispatch = useDispatch();
  const [isValid, setIsValid] = useState(true);
  const [optionalCheckBox, setOptionalCheckBox] = useState({ name: 'optional_check_box', is_active: false, label: ' Optional: sign up to receive sms rate updates from our insurance concierge' });
  const question_types_to_show_button = [QUOTE_FUNNEL.COMPONENT_TYPE.SINGLE_SELECT, QUOTE_FUNNEL.COMPONENT_TYPE.BOOLEAN, QUOTE_FUNNEL.COMPONENT_TYPE.BOOLEAN_WITH_DISPLAY];
  const [customTimeoutSet] = useClearableTimeout();
  const sanitizedTcpaDisclaimer = DOMPurify.sanitize(tcpa_disclaimer_text);

  const emailValidationFunc = async (page, is_skip) => {
    const isEmailPage = page?.question_types[0] === 'email';
    if (isEmailPage && !is_skip) {
      const emailQuestion = page?.question_groups?.flatMap((qGroup) => qGroup.questions
        ?.filter((question) => question.question_type === 'email')
      )[0];
      if (emailQuestion?.value) {
        let isEmailValid = await validate_email_request(emailQuestion)
        setIsValid(isEmailValid)
        return isEmailValid
      }
    }
    setIsValid(true)
    return true
  }

  const {
    answer_agent_profile,
  } = useSelector((state) => ({
    answer_agent_profile: state.QuoteFunnelAnswersAgentProfileReducer.answer_agent_profile,
  }));

  const phoneValidationFunc = async (page, is_skip) => {
    const isPhoneNumPage = page?.question_types[0] === 'phone_number';
    if (isPhoneNumPage && !is_skip) {
      const phoneQuestion = page?.question_groups?.flatMap((qGroup) => qGroup.questions
        ?.filter((question) => question.question_type === 'phone_number')
      )[0];
      if (phoneQuestion?.value) {
        let isPhoneNumValid = await validate_phone_request(phoneQuestion, phoneQuestion?.value)
        setIsValid(isPhoneNumValid)
        return isPhoneNumValid
      }
    }
    setIsValid(true)
    return true
  }

  useEffect(async () => {
    if (review) {
      let isEmailValid = await emailValidationFunc(page, false)
      if (!isEmailValid) {
        return;
      }
      let isPhoneNumValid = await phoneValidationFunc(page, false)
      if (!isPhoneNumValid) {
        return;
      }
    }
  }, [review])

  const doesIncludeAllElements = page?.question_types?.every(element => question_types_to_show_button.includes(element));
  const isValidYear = (year) => {
    const currentYear = new Date().getFullYear();
    return year >= 1900 && year <= currentYear;
  };

  const isValidDate = (mm, dd, yyyy) => {
    // Convert the input values to numbers
    mm = parseInt(mm, 10);
    dd = parseInt(dd, 10);
    yyyy = parseInt(yyyy, 10);

    // Create a new Date object
    const date = new Date(yyyy, mm - 1, dd);

    // Check if the input values match the parsed values and the date object is valid
    return (
      date.getDate() === dd &&
      date.getMonth() === mm - 1 &&
      date.getFullYear() === yyyy
    );
  }

  const handleFlashMessage = () => {
    customTimeoutSet(() => {
      setIsValid(true);
    }, 3000);
  }

  useEffect(() => {
    if (isValid) {
      return;
    }
    handleFlashMessage();
  }, [isValid]);

  const isLastPageAndPageGroup = isLastPage && isLastPageGroup;
  const handleNextPage = async (request_duplicate_pages, is_skip) => {
    let isEmailValid = await emailValidationFunc(page, is_skip)
    if (!isEmailValid) {
      return;
    }
    /*Email Validation end*/

    /*Date Validation starts*/
    const isDatePage = page?.question_types[0] === 'date';
    const dateQuestionArr = page?.question_groups
      ?.flatMap((qGroup) =>
        qGroup.questions
          ?.filter((question) => question.question_type === "date"))
    if (isDatePage && dateQuestionArr?.[0]?.value && !is_skip) {
      const yearValue = dateQuestionArr
        ?.map((question) => {
          const dateParts = question?.value?.split('/');
          const isValid = isValidDate(...dateParts);

          if (!isValid) {
            setIsValid(false)
            return;
          }

          if (dateParts[0] === 'null' || !dateParts[1]) {
            setIsValid(false)
            return;
          }
          const yearPart = dateParts[2];
          return parseInt(yearPart);
        })
        .find((value) => !isNaN(value));

      if (!isValidYear(yearValue)) {
        setIsValid(false)
        return;
      }
    }
    let isPhoneNumValid = await phoneValidationFunc(page, is_skip)
    if (!isPhoneNumValid) {
      return;
    }
    if (isLastPageAndPageGroup) {
      const newConsentCertificateId = document.querySelector('input[name="consent-confirmation-certificate-id"]')?.value
      dispatch(concentFormSubmit({ certificate_id: newConsentCertificateId }));
    }
    nextPage(request_duplicate_pages);
  }

  const handleSkipPage = () => {
    const new_question_groups = page?.question_groups?.map((q_gr, idx) => {
      return {
        ...q_gr,
        questions: q_gr?.questions?.map(question => ({ ...question, value: null, skipped: true }))
      }
    })
    onChangeEvent({
      ...page,
      request_duplicate_pages: new_question_groups?.map(q_gr => q_gr.request_duplicate_pages)?.reduce((a, b) => a || b),
      question_groups: new_question_groups
    })
    handleNextPage(false, true);
  }

  const isAnyRequiredQuestionEmpty = page?.question_groups?.some((q_gr) =>
    q_gr.questions?.some((question) => question.required && !question.value)
  );
  const handleChange = (val, index) => {
    const new_question_groups = page?.question_groups?.map((q_gr, idx) => {
      if (idx !== index) {
        return q_gr;
      }
      return val;
    })
    onChangeEvent({
      ...page,
      request_duplicate_pages: new_question_groups?.map(q_gr => q_gr.request_duplicate_pages)?.reduce((a, b) => a || b),
      question_groups: new_question_groups
    });

    if (doesIncludeAllElements) {
      handleNextPage(val?.request_duplicate_pages, false);
    }
  };
  let carrier_value = answers?.find(ans => (ans?.field_name === CARRIER))?.value
  let continuousInsuranceQuestion;
  page?.question_groups?.forEach((qGroup) => {
    continuousInsuranceQuestion = qGroup?.questions?.find((question) => question?.field_name === CONTINUOUS_INSURANCE);
  });
  const QUOTE_FUNNEL_LABEL = `How long have you been with ${carrier_value}? `
  const handleKeyDown = (event) => {
    if (event?.key === 'Enter' && !isAnyRequiredQuestionEmpty) {
      handleNextPage(false, false);
    }
  };
  return (
    <Row id={`quote-form-page-${nextPage}`} onKeyDown={handleKeyDown}>
      {page?.question_groups?.sort((a, b) => a.order - b.order)?.map((q_gr, idx) => {
        return (
          <Col md={12} className="mb-3" key={`question_group_wrapper_${idx}`}>
            {q_gr?.label && (
              <LabelWithTooltipComponent
                name={(continuousInsuranceQuestion ? QUOTE_FUNNEL_LABEL : q_gr?.label) || ''}
                htmlFor={q_gr?.id || "_temp"}
                target={"label_" + (q_gr?.id)}
                is_active={!!q_gr?.is_active_tooltip}
                tooltip_message={q_gr?.tooltip || ''}
              />
            )}
            <QuestionGroup
              answers={answers}
              index={idx}
              question_group={q_gr}
              key={`question_group_${idx}`}
              onChangeEvent={(value) => handleChange(value, idx)}
            />
          </Col>
        )
      })}
      {
        !isValid && questionTypeLabels[page?.question_types[0]] && (
          <span className='quote-funnel-invalid-values'>
            {`Please enter a valid ${questionTypeLabels[page?.question_types[0]]}`}
          </span>
        )
      }
      {quote_funnel_questions_loading && <LoadingSpinner spinner_div_id="alt_status" />}
      {!quote_funnel_questions_loading && !doesIncludeAllElements && (
        <Col md={12} className="mb-3 d-flex justify-content-around align-items-center">
          <Button
            type="button"
            onClick={() => handleNextPage(false, false)}
            onKeyDown={handleKeyDown}
            disabled={isAnyRequiredQuestionEmpty}
            className={`btn quote-funnel-answer-btn-block btn-lg quote-funnel-answer-btn ${isLastPageAndPageGroup ? 'consent-confirmation-form-submit' : ''}`}
          >{isLastPageAndPageGroup ? CONTINUE_AND_GET_QUOTE_TEXT : 'Continue'}</Button>
        </Col>)} 
      {/* if a page has required question, then we are not showing this button */}
      {page?.skip && !isAnyRequiredQuestionEmpty && !isLastPageAndPageGroup && (
        <Col md={12} className="mb-3 d-flex justify-content-around align-items-center">
          <p onClick={() => handleSkipPage()} style={{ 'fontSize': '16px' }} className='btn btn-link text-sfblue font-weight-semibold'>I'd rather not say</p>
        </Col>
      )}

      {isLastPageAndPageGroup && (
        <>
          <input 
            type="checkbox"
            id="companies_1"
            value="true"
            checked="true"
            className="consent-confirmation-brand"
            data-brand-name={answer_agent_profile?.name}
            style={{ position: 'absolute', visibility: 'hidden', height: 0, width: 0, margin: 0, padding: 0 }}
          />
          <Col md={12} sm={12} className='my-3'>
            <SfCustomCheckBox
              filter={optionalCheckBox}
              custom_css={'d-flex justify-content-center'}
              index={1}
              handle_checkbox_value={(value) => setOptionalCheckBox({ ...optionalCheckBox, is_active: value })} />
          </Col>
          {sanitizedTcpaDisclaimer && <Col
            md={12}
            id="leadid_tcpa_disclosure"
            className='consent-confirmation-tcpa-disclosure'
            dangerouslySetInnerHTML={{ __html: sanitizedTcpaDisclaimer }}>
          </Col>}
        </>
      )
      }
    </Row>
  )
}

export default Page;
